import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../lib/firebase";

interface ModuleConfig {
  moduleSource?: any;
  Enabled?: boolean;
  [key: string]: any;
}

interface ModuleInfo {
  ref: any;
  data: any;
}

/**
 * Service to migrate and fix compliance modules for customers with old data structures
 */
export class ComplianceModuleMigrationService {
  
  /**
   * Fix compliance modules by adding proper moduleSource references
   * This is needed for customers with old data structures that lack moduleSource
   */
  static async fixComplianceModules(customerID: string): Promise<boolean> {
    try {
      console.log('Starting compliance modules migration for customer:', customerID);
      
      // Get current compliance modules
      const complianceModulesRef = doc(db, 'customers', customerID, 'companySettings', 'complianceModules');
      const complianceModulesDoc = await getDoc(complianceModulesRef);
      
      if (!complianceModulesDoc.exists) {
        console.log('No compliance modules found for customer:', customerID);
        return true; // Nothing to fix
      }
      
      const currentModules = complianceModulesDoc.data() || {};
      let hasChanges = false;
      const updatedModules = { ...currentModules };
      
      // Get all available modules from the modules collection
      const modulesCollection = collection(db, 'modules');
      const modulesSnapshot = await getDocs(modulesCollection);
      const modulesBySlug = new Map<string, ModuleInfo>();
      const modulesByLegacyKey = new Map<string, ModuleInfo>();
      
      // Build lookup maps for modules
      modulesSnapshot.docs.forEach(docSnapshot => {
        const moduleData = docSnapshot.data();
        const moduleRef = doc(db, 'modules', docSnapshot.id);
        
        // Map by slug
        if (moduleData.slug) {
          modulesBySlug.set(moduleData.slug, { ref: moduleRef, data: moduleData });
        }
        
        // Map by legacy keys for backward compatibility
        const legacyKeys = this.getModuleConfigKeys(moduleData.slug || '', moduleData);
        legacyKeys.forEach(key => {
          modulesByLegacyKey.set(key, { ref: moduleRef, data: moduleData });
        });
      });
      
      // Process each compliance module
      for (const [moduleKey, moduleConfig] of Object.entries(currentModules)) {
        if (typeof moduleConfig !== 'object' || moduleConfig === null) {
          continue;
        }
        
        const typedModuleConfig = moduleConfig as ModuleConfig;
        
        // Check if moduleSource is missing or invalid
        if (!typedModuleConfig.moduleSource || !typedModuleConfig.moduleSource.path) {
          console.log(`Fixing moduleSource for module: ${moduleKey}`);
          
          // Try to find the module by key
          let moduleInfo = modulesByLegacyKey.get(moduleKey);
          
          if (moduleInfo) {
            // Add moduleSource and merge with defaultSettings
            updatedModules[moduleKey] = {
              ...moduleConfig,
              moduleSource: moduleInfo.ref
            };
            hasChanges = true;
            console.log(`Fixed moduleSource for ${moduleKey}`);
          } else {
            console.warn(`Could not find module reference for key: ${moduleKey}`);
          }
        }
      }
      
      // Save changes if any were made
      if (hasChanges) {
        await setDoc(complianceModulesRef, updatedModules, { merge: true });
        console.log('Compliance modules migration completed successfully');
        return true;
      } else {
        console.log('No compliance modules needed fixing');
        return true;
      }
      
    } catch (error) {
      console.error('Error during compliance modules migration:', error);
      throw error;
    }
  }
  
  /**
   * Get all possible key formats for a module in complianceModules
   * This matches the logic from the backend template service
   */
  private static getModuleConfigKeys(moduleId: string, moduleData: any): string[] {
    const keys: string[] = [];
    
    // Map module IDs to their representation in complianceModules
    switch (moduleId) {
      case 'iso-iec-27001':
        keys.push('iso27001-2013', 'ISO27001');
        break;
      case 'disp':
        keys.push('disp', 'DISP');
        break;
      case 'essential-eight':
        keys.push('e8', 'essential8');
        break;
      case 'privacy-act':
        keys.push('privacyAct', 'pa');
        break;
      case 'iso-iec-27701':
        keys.push('iso27701');
        break;
      case 'iso-9001':
        keys.push('iso9001');
        break;
      case 'iso-14001':
        keys.push('iso14001');
        break;
      case 'iso-45001':
        keys.push('iso45001');
        break;
      case 'iso-42001':
        keys.push('iso42001');
        break;
      case 'ism':
        keys.push('ism');
        break;
      case 'nist-csf':
        keys.push('nistcsf');
        break;
      case 'nist-800-53':
        keys.push('nist80053');
        break;
      case 'soc2':
        keys.push('soc2');
        break;
      case 'smb-1001':
        keys.push('SMB1001');
        break;
      default:
        // Use the moduleId as default and look for mappings in module data
        keys.push(moduleId);
        
        // Check if module specifies any legacy keys
        if (moduleData.legacyKeys && Array.isArray(moduleData.legacyKeys)) {
          moduleData.legacyKeys.forEach((key: string) => keys.push(key));
        }
    }
    
    return keys;
  }
}

export default ComplianceModuleMigrationService;
